/**
 * Klaviyo wishlist bridge — Swish new SDK.
 *
 * Subscribes to the Swish `item-create` event (fired when a customer saves a
 * product) and sends a `BLK | Added to Wishlist` custom event to Klaviyo with
 * the same payload shape used by the previous Code Access integration.
 *
 * The Swish `item-create` detail carries only Shopify GIDs; product data is
 * resolved lazily via `swish.state.product` so this module never blocks the
 * SDK boot or the page's critical path.
 *
 * @see https://swish.app/docs/references/sdk/events.md
 * @see https://swish.app/docs/references/sdk/state.md
 */

interface KlaviyoOnsite {
  push: (command: unknown[]) => void;
}

// ── Minimal Swish SDK type stubs ─────────────────────────────────────────────

interface SwishItem {
  id: string;
  /** Shopify GID, e.g. "gid://shopify/Product/8234567890" */
  productId: string;
  /** Shopify GID or null when no variant was selected. */
  variantId: string | null;
  quantity: number;
  createdAt: string;
}

interface SwishMoney {
  amount: string; // major-unit decimal, e.g. "49.90"
  currencyCode: string;
}

interface SwishStorefrontProduct {
  id: string;
  title: string;
  handle: string;
  onlineStoreUrl: string;
  /** Standard Storefront API field — present in Swish's cached product data. */
  vendor?: string;
  featuredImage: { url: string; altText: string | null } | null;
  priceRange: { minVariantPrice: SwishMoney };
  compareAtPriceRange: { minVariantPrice: SwishMoney };
}

interface SwishStorefrontVariant {
  id: string;
  price: SwishMoney | null;
  compareAtPrice: SwishMoney | null;
  image: { url: string; altText: string | null } | null;
}

interface SwishProductSignal {
  value: {
    product: SwishStorefrontProduct | null;
    variant: SwishStorefrontVariant | null;
    loading: boolean;
  };
  /** Release reactive subscriptions for this signal. */
  dispose?: () => void;
}

interface SwishSDK {
  events: {
    subscribe: (
      event: string | string[],
      handler: (e: CustomEvent<SwishItem>) => void,
    ) => () => void;
  };
  state: {
    product: (opts: {
      productId: string;
      variantId?: string | null;
    }) => SwishProductSignal;
    effect: (fn: () => void) => (() => void) | void;
  };
}

declare global {
  interface Window {
    klaviyo?: KlaviyoOnsite;
    _klOnsite?: unknown[][];
    swish?: SwishSDK;
  }
}

// ── Constants ────────────────────────────────────────────────────────────────

const KLAVIYO_WISHLIST_ADD = 'BLK | Added to Wishlist';

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extract trailing numeric id from a Shopify GID.
 * "gid://shopify/Product/8234567890" → "8234567890"
 * Falls back to the raw string if no match.
 */
function numericId(gid: string): string {
  const match = gid.match(/\/(\d+)$/);
  return match ? match[1] : gid;
}

function trackWishlistAdd(
  data: SwishStorefrontProduct,
  variant: SwishStorefrontVariant | null,
  productGid: string,
): void {
  // Storefront API amounts are already major-unit strings — do not divide.
  const price =
    variant?.price?.amount ??
    data.priceRange?.minVariantPrice?.amount ??
    '';

  const compareAt =
    variant?.compareAtPrice?.amount ??
    data.compareAtPriceRange?.minVariantPrice?.amount ??
    '';

  const imageUrl = variant?.image?.url ?? data.featuredImage?.url ?? '';

  const productUrl = data.onlineStoreUrl?.startsWith('http')
    ? data.onlineStoreUrl
    : `${window.location.origin}/products/${data.handle}`;

  const pid = numericId(productGid);

  const properties: Record<string, string | number> = {
    ProductName: data.title,
    // Keep numeric for existing Klaviyo flow compatibility.
    ProductID: /^\d+$/.test(pid) ? Number(pid) : pid,
    ImageURL: imageUrl,
    URL: productUrl,
    Brand: data.vendor ?? '',
    Price: price,
  };

  // Only include CompareAtPrice when there is an actual markdown.
  if (compareAt && Number(compareAt) > Number(price)) {
    properties.CompareAtPrice = compareAt;
  }

  const command = ['track', KLAVIYO_WISHLIST_ADD, properties];

  if (window.klaviyo && typeof window.klaviyo.push === 'function') {
    window.klaviyo.push(command);
    return;
  }

  // Klaviyo not yet loaded — queue for deferred push.
  window._klOnsite = window._klOnsite || [];
  window._klOnsite.push(command);
}

// ── Core ─────────────────────────────────────────────────────────────────────

function attachSwishKlaviyo(sdk: SwishSDK): void {
  sdk.events.subscribe('item-create', (event) => {
    const item = event.detail;

    const productSignal = sdk.state.product({
      productId: item.productId,
      variantId: item.variantId,
    });

    // Track exactly once when product data becomes available.
    let tracked = false;
    sdk.state.effect(() => {
      const { product: data, variant } = productSignal.value;
      if (!data || tracked) return;

      tracked = true;
      trackWishlistAdd(data, variant, item.productId);

      // Release the reactive subscription; we don't need live updates.
      productSignal.dispose?.();
    });
  });
}

// ── Boot ─────────────────────────────────────────────────────────────────────

if (window.swish) {
  // SDK already booted before this module ran.
  attachSwishKlaviyo(window.swish);
} else {
  document.addEventListener(
    'swish-ready',
    () => {
      if (window.swish) attachSwishKlaviyo(window.swish);
    },
    { once: true },
  );
}
